import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import mixpanel from "mixpanel-browser";

import { parseExcelFile } from "utils/create/takeoff/parseExcel";
import { convertExcelToModelData } from "utils/create/takeoff/convertExcelToModelData";
import { useStateSelector } from "utils/field";

import { actions as modelActions } from "features/Model/_ducks";
import { actions as resultsActions } from "features/Model/Review/Results/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { actions as userActions } from "store/users";
import { clearDrawingData } from "features/Model/DrawingNew/_ducks/actions";
import { resetForm } from "store/form/actions";

import Button from "components/Button";
import InputRow from "components/Input/Row";
import SelectFolder from "components/SelectModelFolder";

import classes from "../style.module.scss";

const { fetchModelData, createModel, modelLoading, setExportErrors, clearModelData } = modelActions;
const { resetResults } = resultsActions;
const { clearUpgradeData } = upgradeActions;
const { fetchUserDir } = userActions;

const TakeoffUpload = ({
    history,
    hasFiveModels,
    clearModelData,
    clearDrawingData,
    resetResults,
    clearUpgradeData,
    setExportErrors,
    modelLoading,
    fetchModelData,
    fetchUserDir,
    createModel,
    resetForm,
    currentFolderId,
    setCurrentFolderId,
}) => {
    const [excelParseResult, setExcelParseResult] = useState(null);
    const [parsingExcel, setParsingExcel] = useState(false);
    const [selectedExcelFile, setSelectedExcelFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [isSelectingFolderOpen, setIsSelectingFolderOpen] = useState(false);

    const { modelDir: { folders = {} } = {}, uid, name: username } = useStateSelector("user");

    useEffect(() => {
        fetchUserDir(uid);
    }, []);

    const handleExcelFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedExcelFile(file);
            setExcelParseResult(null);
        }
    };

    const handleParseExcel = async () => {
        if (!selectedExcelFile) {
            setExcelParseResult({ error: "Please select an Excel file first." });
            return;
        }

        setParsingExcel(true);
        setExcelParseResult(null);

        try {
            const parsedData = await parseExcelFile(selectedExcelFile, "Volta SNAP Inputs", 2);

            // Convert to modelData
            const { modelName, modelData } = convertExcelToModelData(parsedData);

            setExcelParseResult({
                parsedData,
                modelData,
                modelName,
                message: "Excel file parsed and converted to modelData successfully!",
            });
            // console.log("Converted modelData:", modelData);
            // console.log("Parsed Excel data:", parsedData);
        } catch (error) {
            console.error("Failed to parse Excel file:", error);
            setExcelParseResult({
                error: error.message,
            });
        } finally {
            setParsingExcel(false);
        }
    };

    // Calculate component summary
    const getComponentSummary = () => {
        if (!excelParseResult?.modelData?.components) {
            return null;
        }

        const components = excelParseResult.modelData.components;

        // Count floor headers from all components' subcomponents
        let floorHeaderCount = 0;
        Object.keys(components).forEach((compType) => {
            Object.keys(components[compType] || {}).forEach((compId) => {
                const comp = components[compType][compId];
                if (comp.subcomponents?.floorHeader) {
                    floorHeaderCount += Object.keys(comp.subcomponents.floorHeader).length;
                }
            });
        });

        const summary = {
            walls: Object.keys(components.wall || {}).length,
            basements: Object.keys(components.basement || {}).length,
            crawlspaces: Object.keys(components.crawlspace || {}).length,
            ceilings: Object.keys(components.ceiling || {}).length,
            expFloors: Object.keys(components.expFloor || {}).length,
            slabs: Object.keys(components.slab || {}).length,
            doors: excelParseResult.parsedData?.doors?.length || 0,
            windows: excelParseResult.parsedData?.windows?.length || 0,
            floorHeaders: floorHeaderCount,
        };

        return summary;
    };

    const handleSubmit = async () => {
        if (!excelParseResult?.modelData) {
            return;
        }

        setSubmitting(true);

        const { modelName, modelData } = excelParseResult;

        const modelId = await createModel(
            {
                name: modelName || "Imported Model",
                modelData,
                parentFolderId: currentFolderId,
                createdAt: moment().format(),
                sharedWith: [uid],
                template: "Takeoff Upload",
                owner: {
                    name: username,
                    uid: uid,
                },
            },
            uid,
            currentFolderId
        ).then((id) => id);

        await clearModelData();
        await clearDrawingData();
        await resetResults();
        await clearUpgradeData();
        await setExportErrors([]);
        await modelLoading();
        await fetchModelData(modelId);

        mixpanel.track("Create Model from Takeoff", {
            Source: "Excel Takeoff Upload",
        });
        setSubmitting(false);

        resetForm("create");

        return history.push(`/models/${modelId}`);
    };

    const componentSummary = getComponentSummary();
    const canCreateModel = hasFiveModels || submitting || !excelParseResult?.modelData;

    return (
        <div className={classes.createForm}>
            <div className={classes.sectionHeader}>
                <h3>Upload Takeoff Excel File</h3>
            </div>
            <InputRow>
                <div
                    style={{
                        width: "100%",
                        border: "2px dashed #62bcf8",
                        borderRadius: "4px",
                        padding: "2rem",
                        textAlign: "center",
                        backgroundColor: "#fff",
                        cursor: parsingExcel ? "not-allowed" : "pointer",
                        opacity: parsingExcel ? 0.6 : 1,
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onDragEnter={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (parsingExcel) return;
                        const file = e.dataTransfer.files[0];
                        if (file && (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) {
                            setSelectedExcelFile(file);
                            setExcelParseResult(null);
                        }
                    }}
                >
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelFileSelect}
                        disabled={parsingExcel}
                        style={{ display: "none" }}
                        id="excel-file-input"
                    />
                    <label
                        htmlFor="excel-file-input"
                        style={{
                            cursor: parsingExcel ? "not-allowed" : "pointer",
                            display: "block",
                        }}
                    >
                        {selectedExcelFile ? (
                            <div>
                                <p style={{ margin: "0.5rem 0", fontWeight: 500 }}>
                                    Selected: {selectedExcelFile.name}
                                </p>
                                <Button
                                    onClick={handleParseExcel}
                                    disabled={parsingExcel}
                                    style={{ marginTop: "1rem" }}
                                >
                                    {parsingExcel ? "Parsing..." : "Parse Excel File"}
                                </Button>
                            </div>
                        ) : (
                            <div>
                                <p style={{ margin: "0.5rem 0" }}>
                                    Drag and drop an Excel file here or{" "}
                                    <span style={{ color: "#0049c6", textDecoration: "underline" }}>browse</span>
                                </p>
                                <p style={{ fontSize: "0.875rem", color: "#666", margin: "0.5rem 0" }}>(.xlsx, .xls)</p>
                            </div>
                        )}
                    </label>
                </div>
            </InputRow>
            {excelParseResult?.error && (
                <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                    <p style={{ color: "#c61717" }}>Error: {excelParseResult.error}</p>
                </div>
            )}
            {componentSummary && (
                <div style={{ marginTop: "1.5rem", padding: "1rem", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                    <h5 style={{ marginBottom: "0.75rem", fontSize: "1rem", fontWeight: 600 }}>
                        Takeoff Summary: <span style={{ fontWeight: 400 }}>{excelParseResult.modelName}</span>
                    </h5>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "0.75rem",
                            fontSize: "0.875rem",
                            marginBottom: "0.75rem",
                        }}
                    >
                        <div>
                            <strong>Basements:</strong> {componentSummary.basements}
                        </div>
                        <div>
                            <strong>Crawlspaces:</strong> {componentSummary.crawlspaces}
                        </div>
                        <div>
                            <strong>Slabs:</strong> {componentSummary.slabs}
                        </div>
                        <div></div>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "0.75rem",
                            fontSize: "0.875rem",
                            marginBottom: "0.75rem",
                        }}
                    >
                        <div>
                            <strong>Walls:</strong> {componentSummary.walls}
                        </div>
                        <div>
                            <strong>Ceilings:</strong> {componentSummary.ceilings}
                        </div>
                        <div>
                            <strong>Exposed Floors:</strong> {componentSummary.expFloors}
                        </div>
                        <div></div>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "0.75rem",
                            fontSize: "0.875rem",
                        }}
                    >
                        <div>
                            <strong>Doors:</strong> {componentSummary.doors}
                        </div>
                        <div>
                            <strong>Windows:</strong> {componentSummary.windows}
                        </div>
                        <div>
                            <strong>Floor Headers:</strong> {componentSummary.floorHeaders}
                        </div>
                        <div></div>
                    </div>
                </div>
            )}
            <InputRow
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    fontSize: "0.875rem",
                    marginTop: "1.5rem",
                }}
            >
                <p>
                    Current folder -{" "}
                    <strong>
                        {currentFolderId && folders && folders[currentFolderId]?.name
                            ? folders[currentFolderId].name
                            : currentFolderId && folders && !folders[currentFolderId]?.name
                            ? "Unknown Folder"
                            : "Model Directory"}
                    </strong>
                </p>
                <p
                    style={{
                        cursor: "pointer",
                        color: "#0049c6",
                        fontWeight: 500,
                    }}
                    className={classes.folderText}
                    onClick={() => setIsSelectingFolderOpen(!isSelectingFolderOpen)}
                >
                    Select Folder
                </p>
            </InputRow>
            <Button
                large
                className={classes.createButton}
                onClick={handleSubmit}
                disabled={canCreateModel}
                style={{ marginTop: "1.5rem" }}
            >
                {submitting ? "Creating Model..." : "Create Model"}
            </Button>
            <SelectFolder
                folderToMove={isSelectingFolderOpen}
                folders={folders}
                close={() => setIsSelectingFolderOpen(!isSelectingFolderOpen)}
                isMoving={false}
                onSelect={(selectedFolder) => setCurrentFolderId(selectedFolder)}
            />
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    resetForm: (formId) => dispatch(resetForm(formId)),
    clearModelData: () => dispatch(clearModelData()),
    clearDrawingData: () => dispatch(clearDrawingData()),
    resetResults: () => dispatch(resetResults()),
    clearUpgradeData: () => dispatch(clearUpgradeData()),
    setExportErrors: () => dispatch(setExportErrors()),
    modelLoading: () => dispatch(modelLoading()),
    fetchModelData: (modelId) => dispatch(fetchModelData(modelId)),
    fetchUserDir: (uid) => dispatch(fetchUserDir(uid)),
    createModel: (modelData, uid, parentFolderId) => dispatch(createModel(modelData, uid, parentFolderId)),
});

export default connect(null, mapDispatchToProps)(TakeoffUpload);
